/**
 * Centralised, typed access to environment configuration.
 *
 * All runtime configuration flows through this module so that the rest of the
 * application never reads `import.meta.env` directly. This keeps configuration
 * discoverable, typed, and easy to mock in tests.
 */

function readString(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

/**
 * Authentication mode:
 *  - `bff`  → real OAuth2-BFF session auth (cookie session managed by the BFF;
 *             session/permissions/tenants are bootstrapped from `GET /api/v1/me`).
 *  - `mock` → in-browser mock session (no backend) for local UI work and tests.
 *
 * Defaults to `mock` so the app and the test suite run offline; production
 * builds set `VITE_AUTH_MODE=bff`.
 */
export type AuthMode = 'bff' | 'mock';

function readAuthMode(value: string | undefined): AuthMode {
  return value?.trim().toLowerCase() === 'bff' ? 'bff' : 'mock';
}

/**
 * Parses `VITE_MOCK_FEATURE_FLAGS`, an optional JSON object string used to simulate the
 * platform-managed merchant-portal feature flags (`plan-tenant-003`) while running in `mock` auth
 * mode — e.g. `'{"merchantPortal.nav.orders":true}'`. This mirrors the flat `key -> boolean` map the
 * real BFF returns as `MeResponse.featureFlags` in `bff` mode, so `MockSessionProvider` can stand in
 * for a platform manager having set flags globally/per-tenant without a backend.
 *
 * Never throws. Returns `null` — treated as "no flags set", so the SPA's own
 * `flag ?? codeDefault` resolvers (`merchantPortalNavVisibility.ts`) fall back to their code
 * defaults — when the variable is absent/blank, is not valid JSON, or does not parse to a flat
 * object of only boolean values; any of those cases is reported once via `console.warn` so a typo
 * in a local `.env` is easy to notice instead of silently doing nothing.
 */
function readMockFeatureFlags(value: string | undefined): Record<string, boolean> | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    console.warn(`[env] VITE_MOCK_FEATURE_FLAGS is not valid JSON and will be ignored: ${trimmed}`);
    return null;
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.warn(
      '[env] VITE_MOCK_FEATURE_FLAGS must be a flat JSON object of booleans (e.g. ' +
        '\'{"merchantPortal.nav.orders":true}\') and will be ignored.',
    );
    return null;
  }

  const flags = parsed as Record<string, unknown>;
  const hasNonBooleanValue = Object.values(flags).some(
    (flagValue) => typeof flagValue !== 'boolean',
  );
  if (hasNonBooleanValue) {
    console.warn(
      '[env] VITE_MOCK_FEATURE_FLAGS must only contain boolean values and will be ignored.',
    );
    return null;
  }

  return flags as Record<string, boolean>;
}

export interface AppEnv {
  /** Base URL of the API gateway / BFF. */
  readonly apiBaseUrl: string;
  /** Human-readable application name. */
  readonly appName: string;
  /** Default tenant identifier used until a real tenant is resolved. */
  readonly defaultTenantId: string;
  /** Active authentication mode. */
  readonly authMode: AuthMode;
  /**
   * Base URL of the Keycloak Account Console realm root (e.g.
   * `https://auth.onebazaar.ru/realms/<realm>/account/`), where a signed-in user manages their own
   * profile, password and 2FA. The header user menu appends `referrer`/`referrer_uri` query params
   * before linking to it (see `buildAccountConsoleUrl` in `AppLayout.tsx`) so the console shows a
   * native "Back to Merchant Portal" link. `auth.onebazaar.ru` is the only Keycloak host reachable
   * by end users — never the internal server URL services use for issuer validation.
   */
  readonly accountConsoleUrl: string;
  /**
   * Simulated merchant-portal feature-flag overrides for `mock` auth mode only (parsed from
   * `VITE_MOCK_FEATURE_FLAGS`). `null` when unset or invalid. Has no effect in `bff` mode — the
   * real session there is sourced from `GET /api/v1/me`.
   */
  readonly mockFeatureFlags: Record<string, boolean> | null;
  /** Active Vite mode (e.g. "development", "production", "test"). */
  readonly mode: string;
  /** True when running a production build. */
  readonly isProduction: boolean;
  /** True when running in development. */
  readonly isDevelopment: boolean;
}

export const env: AppEnv = {
  apiBaseUrl: readString(import.meta.env.VITE_API_BASE_URL, 'http://localhost:8080'),
  appName: readString(import.meta.env.VITE_APP_NAME, 'Merchant Portal'),
  defaultTenantId: readString(import.meta.env.VITE_DEFAULT_TENANT_ID, 'demo-tenant'),
  authMode: readAuthMode(import.meta.env.VITE_AUTH_MODE),
  accountConsoleUrl: readString(
    import.meta.env.VITE_ACCOUNT_CONSOLE_URL,
    'https://auth.onebazaar.ru/realms/dev-obzr-merchants/account/',
  ),
  mockFeatureFlags: readMockFeatureFlags(import.meta.env.VITE_MOCK_FEATURE_FLAGS),
  mode: import.meta.env.MODE,
  isProduction: import.meta.env.PROD,
  isDevelopment: import.meta.env.DEV,
};
